import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { formatPesos } from "@/lib/currency";
import { formatPoints } from "@/lib/format";
import { toast } from "sonner";
import {
    User, Trophy, TrendingUp, Flame, Users, Target, Sparkles, Crown, Gem,
    Shield, ChevronRight, Calendar, CheckCircle2, Circle, Camera, Pencil,
    UserPlus, Award, BarChart3, MailCheck, MailX,
} from "lucide-react";

const LEVEL_ICONS = { novato: Sparkles, amateur: Shield, pro: TrendingUp, elite: Crown, black: Gem };

export default function MyProfile() {
    const { user: me, refresh } = useAuth();
    const [data, setData] = useState(null);
    const [missions, setMissions] = useState(null);
    const [savingName, setSavingName] = useState(false);
    const [editingName, setEditingName] = useState(false);
    const [name, setName] = useState("");
    const [savingPublic, setSavingPublic] = useState(false);

    const load = async () => {
        try {
            const [{ data: p }, { data: m }] = await Promise.all([
                api.get("/me/profile"),
                api.get("/me/missions"),
            ]);
            setData(p);
            setMissions(m);
            setName(p.user.username || "");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo cargar el perfil");
        }
    };
    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const [{ data: p }, { data: m }] = await Promise.all([
                    api.get("/me/profile"),
                    api.get("/me/missions"),
                ]);
                if (cancel) return;
                setData(p);
                setMissions(m);
                setName(p.user.username || "");
            } catch (e) {
                if (cancel) return;
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo cargar el perfil");
            }
        })();
        return () => { cancel = true; };
    }, []);

    if (!me || me === false) return (
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
            <div className="text-p2w-gray">Inicia sesión para ver tu perfil.</div>
            <Link to="/login" className="mt-4 inline-block text-p2w-green font-semibold hover:underline">Entrar</Link>
        </div>
    );
    if (!data) return <div className="max-w-5xl mx-auto px-4 py-12"><div className="h-96 rounded-2xl bg-p2w-surface border border-p2w-line animate-pulse" /></div>;

    const onSaveName = async () => {
        if (!name || name === data.user.username) { setEditingName(false); return; }
        setSavingName(true);
        try {
            await api.patch("/me/profile", { username: name });
            toast.success("Nombre actualizado");
            setEditingName(false);
            await refresh?.();
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        } finally { setSavingName(false); }
    };

    const onTogglePublic = async (checked) => {
        setSavingPublic(true);
        try {
            await api.patch("/me/profile", { is_public: checked });
            toast.success(checked ? "Perfil público activado" : "Perfil privado");
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        } finally { setSavingPublic(false); }
    };

    const Lvl = LEVEL_ICONS[data.level.id] || Sparkles;

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-6" data-testid="my-profile-page">
            {/* HEADER */}
            <div className="relative overflow-hidden rounded-3xl border border-p2w-line/80 bg-gradient-to-br from-[#0d0f1a] via-p2w-surface to-[#1a1325] p-6 sm:p-8">
                <div className="absolute -top-32 -right-20 w-[460px] h-[460px] bg-gradient-to-br from-p2w-purple/30 to-transparent blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[360px] h-[360px] bg-gradient-to-tr from-blue-500/15 to-transparent blur-3xl pointer-events-none" />
                <div className="relative flex flex-col sm:flex-row gap-6 items-start">
                    <div className="relative group">
                        <Avatar className="w-24 h-24 sm:w-28 sm:h-28 ring-2 ring-p2w-green/40 shadow-glow-green">
                            <AvatarImage src={data.user.avatar_url} />
                            <AvatarFallback className="bg-p2w-purple text-white text-3xl font-sora font-bold">
                                {(data.user.username || "?").slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                        </Avatar>
                        <button
                            data-testid="upload-avatar-btn"
                            onClick={() => toast.info("Subida de foto disponible próximamente — por ahora usa tu avatar generado.")}
                            className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-p2w-green text-p2w-green-fg flex items-center justify-center shadow-glow-green hover:scale-105 transition"
                            aria-label="Subir foto"
                        >
                            <Camera className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="flex-1 min-w-0 w-full">
                        <div className="flex items-center gap-2 flex-wrap">
                            {editingName ? (
                                <div className="flex items-center gap-2 max-w-xs">
                                    <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-p2w-elevated border-p2w-line text-white h-9" data-testid="username-edit-input" />
                                    <Button size="sm" onClick={onSaveName} disabled={savingName} className="bg-p2w-green text-p2w-green-fg h-9">{savingName ? "..." : "Guardar"}</Button>
                                    <Button size="sm" variant="outline" onClick={() => { setEditingName(false); setName(data.user.username); }} className="h-9 border-p2w-line">Cancelar</Button>
                                </div>
                            ) : (
                                <>
                                    <h1 className="font-sora font-extrabold text-3xl tracking-tight">@{data.user.username}</h1>
                                    <button onClick={() => setEditingName(true)} data-testid="edit-username-btn" className="text-p2w-gray hover:text-p2w-green transition" aria-label="Editar nombre"><Pencil className="w-4 h-4" /></button>
                                </>
                            )}
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold" style={{ background: data.level.color + "22", color: data.level.color, border: `1px solid ${data.level.color}55` }}>
                                <Lvl className="w-3 h-3" /> {data.level.name}
                            </span>
                            {data.user.email_verified
                                ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-p2w-green font-semibold"><MailCheck className="w-3 h-3" /> verificado</span>
                                : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-400 font-semibold"><MailX className="w-3 h-3" /> sin verificar</span>}
                        </div>
                        <p className="mt-1 text-sm text-p2w-gray break-all">{data.user.email}</p>
                        <div className="mt-4">
                            <Link to={`/perfil/${data.user.username}`} className="inline-flex items-center gap-1.5 text-xs text-p2w-purple hover:text-white transition font-semibold" data-testid="view-public-profile">
                                Ver perfil público <ChevronRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                    </div>
                    {/* Balance card */}
                    <div className="w-full sm:w-auto sm:min-w-[260px] rounded-2xl border border-p2w-green/30 bg-gradient-to-br from-p2w-green/10 via-transparent to-transparent p-5 backdrop-blur-sm">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-p2w-gray font-semibold">Saldo disponible</div>
                        <div className="mt-1 flex items-baseline gap-2">
                            <span className="font-sora font-extrabold text-p2w-green text-3xl number-tabular" data-testid="balance-points">{formatPoints(data.balance_points)}</span>
                            <span className="text-sm text-p2w-gray font-semibold">pts</span>
                        </div>
                        <div className="mt-0.5 text-[12px] text-p2w-gray number-tabular">≈ {formatPesos(data.balance_points)}</div>
                        <Link to="/wallet/recargar" className="mt-3 inline-block text-xs font-semibold text-p2w-green hover:underline">Recargar →</Link>
                    </div>
                </div>
            </div>

            {/* METRICS GRID 1 */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="metrics-primary">
                <MetricCard icon={Target} label="Entradas" value={data.metrics.entries} />
                <MetricCard icon={Trophy} label="Victorias" value={data.metrics.wins} accent="green" />
                <MetricCard icon={TrendingUp} label="Ganado" value={formatPesos(data.metrics.won_amount)} small accent="green" />
                <MetricCard icon={BarChart3} label="Invertido" value={formatPesos(data.metrics.invested_amount)} small />
                <MetricCard icon={Award} label="ROI" value={`${data.metrics.roi}%`} accent={data.metrics.roi >= 0 ? "green" : "red"} />
            </div>

            {/* METRICS GRID 2 */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="metrics-secondary">
                <MetricCard icon={Target} label="% Acierto" value={`${data.metrics.win_rate}%`} accent="purple" />
                <MetricCard icon={Flame} label="Racha activa" value={data.metrics.active_streak} accent={data.metrics.active_streak >= 3 ? "amber" : "default"} />
                <MetricCard icon={Sparkles} label="Mejor categoría" value={data.best_category ? data.best_category.name : "—"} small subtitle={data.best_category ? `${data.best_category.win_rate}% aciertos` : "Sin datos"} />
                <MetricCard icon={Lvl} label="Nivel · Puntos" value={formatPoints(data.level.points)} subtitle={data.level.name} accent="amber" />
            </div>

            {/* SOCIAL + ACCESOS */}
            <div className="grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 rounded-2xl border border-p2w-line bg-p2w-surface p-5 backdrop-blur-sm">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-p2w-purple" />
                            <h3 className="font-sora font-bold text-white text-base">Red social</h3>
                        </div>
                        <div className="flex items-center gap-3" data-testid="public-profile-toggle">
                            <span className="text-xs text-p2w-gray">Perfil público</span>
                            <Switch checked={!!data.user.is_public} disabled={savingPublic} onCheckedChange={onTogglePublic} />
                        </div>
                    </div>
                    <div className="mt-5 grid grid-cols-3 gap-3">
                        <SocialStat label="Seguidores" value={data.social.followers} />
                        <SocialStat label="Siguiendo" value={data.social.following} />
                        <SocialStat label="Pools" value={data.social.pools} />
                    </div>
                </div>
                <div className="rounded-2xl border border-p2w-line bg-p2w-surface p-5 flex flex-col gap-3">
                    <Link to="/wallet" className="flex items-center justify-between rounded-xl border border-p2w-line bg-p2w-elevated px-4 py-3 hover:border-p2w-purple/50 hover:bg-p2w-purple/5 transition group" data-testid="link-referrals">
                        <div className="flex items-center gap-2.5">
                            <UserPlus className="w-4 h-4 text-p2w-purple" />
                            <span className="text-sm font-semibold text-white">Referidos</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-p2w-gray group-hover:text-white transition" />
                    </Link>
                    <Link to="/creadores" className="flex items-center justify-between rounded-xl border border-p2w-line bg-p2w-elevated px-4 py-3 hover:border-p2w-green/50 hover:bg-p2w-green/5 transition group" data-testid="link-creators">
                        <div className="flex items-center gap-2.5">
                            <Crown className="w-4 h-4 text-p2w-green" />
                            <span className="text-sm font-semibold text-white">Programa Creadores</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-p2w-gray group-hover:text-white transition" />
                    </Link>
                    <Link to="/niveles" className="flex items-center justify-between rounded-xl border border-p2w-line bg-p2w-elevated px-4 py-3 hover:border-amber-400/50 hover:bg-amber-400/5 transition group" data-testid="link-levels">
                        <div className="flex items-center gap-2.5">
                            <Award className="w-4 h-4 text-amber-400" />
                            <span className="text-sm font-semibold text-white">Niveles de la Liga</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-p2w-gray group-hover:text-white transition" />
                    </Link>
                </div>
            </div>

            {/* MISSIONS */}
            {missions && (
                <div className="space-y-4">
                    <MissionGroup
                        title="Misiones diarias"
                        icon={<Calendar className="w-4 h-4 text-blue-400" />}
                        accent="blue"
                        group={missions.daily}
                        resetLabel="se reinician cada día"
                    />
                    <MissionGroup
                        title="Misiones semanales"
                        icon={<Trophy className="w-4 h-4 text-amber-400" />}
                        accent="amber"
                        group={missions.weekly}
                        resetLabel="se reinician cada lunes"
                    />
                </div>
            )}
        </div>
    );
}

function MetricCard({ icon: Icon, label, value, subtitle, small, accent }) {
    const accentMap = {
        green: "text-p2w-green",
        purple: "text-p2w-purple",
        amber: "text-amber-400",
        red: "text-rose-400",
        default: "text-white",
    };
    return (
        <div className="rounded-2xl border border-p2w-line bg-p2w-surface px-4 py-3.5 backdrop-blur-sm hover:border-p2w-purple/30 transition-colors">
            <div className="flex items-center gap-1.5 text-p2w-gray text-[10px] uppercase tracking-wider font-semibold">
                <Icon className="w-3 h-3" /> {label}
            </div>
            <div className={`mt-1.5 font-sora font-extrabold ${small ? "text-base" : "text-2xl"} ${accentMap[accent] || accentMap.default} number-tabular leading-tight truncate`} title={String(value)}>
                {value}
            </div>
            {subtitle && <div className="text-[11px] text-p2w-gray mt-0.5 truncate">{subtitle}</div>}
        </div>
    );
}

function SocialStat({ label, value }) {
    return (
        <div className="text-center rounded-xl bg-p2w-bg/40 border border-p2w-line/60 px-3 py-3">
            <div className="font-sora font-extrabold text-2xl text-white number-tabular">{value}</div>
            <div className="text-[10px] uppercase tracking-wider text-p2w-gray mt-1">{label}</div>
        </div>
    );
}

function MissionGroup({ title, icon, accent, group, resetLabel }) {
    return (
        <div className="rounded-2xl border border-p2w-line bg-p2w-surface backdrop-blur-sm overflow-hidden" data-testid={`mission-group-${accent}`}>
            <div className="px-5 py-3.5 border-b border-p2w-line flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    {icon}
                    <h3 className="font-sora font-bold text-white text-base">{title}</h3>
                </div>
                <div className="flex items-center gap-3 text-xs">
                    <span className="text-p2w-gray">{resetLabel}</span>
                    <span className="font-semibold text-white">{group.completed}/{group.total} completadas</span>
                </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 p-4">
                {group.items.map((m, i) => (
                    <MissionCard key={i} m={m} accent={accent} />
                ))}
            </div>
        </div>
    );
}

function MissionCard({ m, accent }) {
    const accentColor = accent === "amber" ? "bg-amber-400" : "bg-blue-400";
    return (
        <div className={`rounded-xl border ${m.completed ? "border-p2w-green/50 bg-p2w-green/5" : "border-p2w-line bg-p2w-elevated"} p-4 transition-all`}>
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                    {m.completed
                        ? <CheckCircle2 className="w-4 h-4 text-p2w-green shrink-0 mt-0.5" />
                        : <Circle className="w-4 h-4 text-p2w-gray shrink-0 mt-0.5" />}
                    <div className="min-w-0">
                        <div className="font-semibold text-white text-sm">{m.title}</div>
                        <div className="text-xs text-p2w-gray mt-0.5">{m.description}</div>
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <div className="font-sora font-bold text-amber-400 text-sm number-tabular">+{m.reward_pts}</div>
                    <div className="text-[9px] uppercase tracking-wider text-p2w-gray font-semibold">pts liga</div>
                </div>
            </div>
            <div className="mt-3 flex items-center gap-3">
                <Progress
                    value={m.progress_pct}
                    className={`h-2 flex-1 bg-p2w-bg/60 [&>div]:${m.completed ? "bg-p2w-green" : accentColor}`}
                />
                <span className="text-xs font-semibold text-white number-tabular w-12 text-right">{m.current}/{m.target}</span>
            </div>
        </div>
    );
}
